import { DurationFieldDE } from "@/pages/eaf/data-entry/components/form-fields/common/DurationFieldDE";
import DurationInput from "@/pages/eaf/data-entry/components/form-fields/common/DurationInput";
import { useHeatReportContext } from "@/pages/heat-report/context/HeatReportContext";
import { useTableStyles } from "@/pages/heat-report/styles/useTableStyles";
import { getMomentTz } from "@dexteel/mesf-core";
import { Grid2 as Grid, Paper, TextField } from "@mui/material";
import React from "react";
import { AssetCard } from "../../../controls/AssetCard";

const moment = getMomentTz();

// Helper function to format numbers with thousand separators
const formatNumberWithCommas = (
	value: number | string | null | undefined,
): string => {
	if (value === null || value === undefined || value === "") return "";

	const numValue = typeof value === "string" ? parseFloat(value) : value;
	if (isNaN(numValue)) return "";

	// Only apply formatting if number is greater than 9,999
	if (Math.abs(numValue) > 9999) {
		return numValue.toLocaleString("en-US");
	}

	return numValue.toString();
};

export const HeaderData = () => {
	const {
		state: { eafProcessData, heat },
	} = useHeatReportContext();
	const classes = useTableStyles();

	return (
		<Paper className={classes.borderAgGridTable} elevation={0}>
			<Grid container spacing={2} sx={{ p: 1 }}>
				{/* Column 1 - Asset Card */}
				<Grid
					size={{ xs: 12, sm: 6, md: 2 }}
					container
					key={`${eafProcessData.ProcessId}-column-1`}
				>
					<Grid size={{ xs: 12 }} key={`${heat.HeatId}-asset`}>
						<AssetCard
							assetId={eafProcessData.AssetId}
							assetName={eafProcessData.AssetName}
						/>
					</Grid>
				</Grid>

				{/* Column 2 - Start Time & Tap Time */}
				<Grid
					size={{ xs: 12, sm: 6, md: 2 }}
					container
					spacing={1}
					key={`${eafProcessData.ProcessId}-column-2`}
				>
					<Grid size={{ xs: 12 }} container direction="row" spacing={1}>
						<Grid size={{ xs: 12 }} key="start-time">
							<TextField
								label="Start Time"
								value={
									eafProcessData?.Start
										? moment(eafProcessData.Start).format("HH:mm - MM/DD/YYYY")
										: ""
								}
								variant="outlined"
								fullWidth
								InputProps={{
									readOnly: true,
								}}
							/>
						</Grid>
						<Grid size={{ xs: 12 }} key="tap-time">
							<TextField
								label="Tap Time"
								value={
									eafProcessData?.End
										? moment(eafProcessData.End).format("HH:mm - MM/DD/YYYY")
										: ""
								}
								variant="outlined"
								fullWidth
							/>
						</Grid>
					</Grid>
				</Grid>

				{/* Column 3 - Tap To Tap & Power Data */}
				<Grid
					size={{ xs: 12, sm: 6, md: 2 }}
					container
					spacing={1}
					key={`${eafProcessData.ProcessId}-column-3`}
				>
					<Grid size={{ xs: 12 }} container direction="row" spacing={1}>
						<Grid size={{ xs: 12 }} key="tap-to-tap">
							<DurationFieldDE
								label={"Tap To Tap [hh:mm:ss]"}
								value={eafProcessData?.TapToTap ?? ""}
								unit={"minutes"}
								onChange={() => {}}
							/>
						</Grid>
						<Grid size={{ xs: 12 }} key="power-on-off">
							<div className={classes.container}>
								<TextField
									label="Power On [minutes]"
									value={
										eafProcessData?.PowerOn
											? `${eafProcessData.PowerOn} min`
											: ""
									}
									variant="outlined"
									fullWidth
									InputProps={{
										readOnly: true,
									}}
								/>
								<TextField
									label="Power Off [minutes]"
									value={
										eafProcessData?.PowerOff
											? `${eafProcessData.PowerOff} min`
											: ""
									}
									variant="outlined"
									fullWidth
									InputProps={{
										readOnly: true,
									}}
								/>
							</div>
						</Grid>
					</Grid>
				</Grid>

				{/* Column 4 - Delay Time & Foreman */}
				<Grid
					size={{ xs: 12, sm: 6, md: 2 }}
					container
					spacing={1}
					key={`${eafProcessData.ProcessId}-column-4`}
				>
					<Grid size={{ xs: 12 }} container direction="row" spacing={1}>
						<Grid size={{ xs: 12 }} key="delay-time">
							<TextField
								label="Delay Time [min]"
								value={eafProcessData?.DelayTime ?? ""}
								variant="outlined"
								fullWidth
							/>
						</Grid>
						<Grid size={{ xs: 12 }} key="foreman">
							<TextField
								label="Foreman"
								value={eafProcessData?.Foreman ?? ""}
								variant="outlined"
								fullWidth
							/>
						</Grid>
					</Grid>
				</Grid>

				{/* Column 5 - Energy Data */}
				<Grid
					size={{ xs: 12, sm: 6, md: 2 }}
					container
					spacing={1}
					key={`${eafProcessData.ProcessId}-column-5`}
				>
					<Grid size={{ xs: 12 }} container direction="row" spacing={1}>
						<Grid size={{ xs: 12 }} key="mwh">
							<TextField
								label="MWH"
								value={formatNumberWithCommas(eafProcessData?.Energy)}
								variant="outlined"
								fullWidth
							/>
						</Grid>
						<Grid size={{ xs: 12 }} key="kwh-per-ton">
							<TextField
								label="KWH/Ton"
								value={
									eafProcessData?.KWHPerTon
										? parseFloat(eafProcessData.KWHPerTon.toFixed(1))
										: ""
								}
								variant="outlined"
								fullWidth
							/>
						</Grid>
					</Grid>
				</Grid>

				{/* Column 6 - Scrap & Oxygen Data */}
				<Grid
					size={{ xs: 12, sm: 6, md: 2 }}
					container
					spacing={1}
					key={`${eafProcessData.ProcessId}-column-6`}
				>
					<Grid size={{ xs: 12 }} container direction="row" spacing={1}>
						<Grid size={{ xs: 12 }} key="scrap-charged">
							<TextField
								label="Scrap Charged [lbs]"
								value={formatNumberWithCommas(eafProcessData?.ScrapCharged)}
								variant="outlined"
								fullWidth
							/>
						</Grid>
						<Grid size={{ xs: 12 }} key="oxygen-volume">
							<TextField
								label="O2 Volume"
								value={formatNumberWithCommas(
									eafProcessData?.OxygenVolume?.toFixed(1),
								)}
								variant="outlined"
								fullWidth
							/>
						</Grid>
					</Grid>
				</Grid>
			</Grid>
		</Paper>
	);
};
